import { DOCUMENT, DatePipe, NgForOf } from '@angular/common';
import {
  Component,
  Inject,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { ApiService } from '@core/services/api.service';
import { AuthService } from '@core/services/auth.service';
import { ModalUtilsService } from '@core/services/modal-utils.service';
import { ApiServiceHolder } from '@core/utils/api-service-holder';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EventService } from '@shared/services/event.service';
import { LanguageService } from '@shared/services/language.service';
import { MetaManagerService } from '@shared/services/meta-manager.service';
import { PublicGenericService } from '@shared/services/public-generic.service';
import { SearchService } from '@shared/services/search.service';
import { environment } from 'src/environments/environment';
import {
  Auction,
  AuctionView,
  buildAuction,
} from '../articles-manager/interfaces/auction.interface';
import { AuctionViewService } from '../articles-manager/services/auction-view.service';
import { ArticleOffersDialogComponent } from './components/dialogs/article-offers-dialog/article-offers-dialog.component';
import { BidSuccessDialogComponent } from './components/dialogs/bid-success-dialog/bid-success-dialog.component';
import { ContactsDialogComponent } from './components/dialogs/contacts-dialog/contacts-dialog.component';

import { NgClass, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TranslatePipe } from '@ngx-translate/core';
import { NoPaymentMethodDialogComponent } from '@shared/components/no-payment-method-dialog/no-payment-method-dialog.component';
import { SuggestedAuctionsComponent } from '@shared/components/suggested-auctions/suggested-auctions.component';
import { ImagePreloadService } from '@shared/services/image-preload.service';
import { PlatformService } from '@shared/services/platform-service.service';
import { RedirectService } from '@shared/services/redirect.service';
import { SEOService } from '@shared/services/seo.service';
import { NgxMaskDirective } from 'ngx-mask';
import { timer } from 'rxjs';
import slugify from 'slugify';
import { TagsComponent } from '../shared/components/tags/tags.component';
import { ArticleBidComponent } from './components/article-bid/article-bid.component';
import { ArticleDescriptionComponent } from './components/article-description/article-description.component';
import { ArticleGalleryComponent } from './components/article-gallery/article-gallery.component';
import { ArticlePropertiesComponent } from './components/article-properties/article-properties.component';
import { DialogArticleType } from './interfaces/article-bid.interface';

@Component({
  selector: 'app-article',
  templateUrl: './article.component.html',
  styleUrls: ['./article.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    NgForOf,
    NgClass,
    FormsModule,
    FaIconComponent,
    TranslatePipe,
    NgxMaskDirective,
    SuggestedAuctionsComponent,
    NoPaymentMethodDialogComponent,
    TagsComponent,
    ArticleGalleryComponent,
    ArticleBidComponent,
    ArticleDescriptionComponent,
    ArticlePropertiesComponent,
  ],
})
@UntilDestroy()
export class ArticleComponent implements OnInit {
  private readonly searchService = inject(SearchService);
  private readonly modalUtils = inject(ModalUtilsService);
  private readonly auctionViewService = inject(AuctionViewService);
  private readonly route = inject(ActivatedRoute);
  private readonly seoService = inject(SEOService);
  private readonly redirectService = inject(RedirectService);
  private readonly languageService = inject(LanguageService);
  private readonly publicGenericService = inject(PublicGenericService);
  private readonly datePipe = inject(DatePipe);
  private readonly metaManagerService = inject(MetaManagerService);
  private readonly _api = inject(ApiService);
  private readonly eventService = inject(EventService);
  public readonly authService = inject(AuthService);

  faEdit = faEdit;

  isLoaded = signal(false);
  id = signal<number | null>(null);
  isMemorabid = computed(() => this.selectedAuction()?.idAuctionType == 1);
  minimumOffer = signal<number | null>(null);
  slug = signal<string | null>(null);

  selectedAuction = signal<Auction>(buildAuction({}));

  sportIds = signal<string | null>(null);
  competitionIds = signal<string | null>(null);
  teamIds = signal<string | null>(null);
  isOwnerTenant = computed<boolean>(() => {
    return this.selectedAuction().idTenantEntity === 1;
  });

  title = computed(
    () =>
      this.selectedAuction().article.articleInfoList.find(
        (info) =>
          info.languageCode == this.languageService.userSelectedLanguage.code
      )?.title
  );

  memorabidAuctionSuggestions: AuctionView[] = [];
  marketplaceAuctionSuggestions: AuctionView[] = [];

  get inPreEventStatus(): boolean {
    if (!!this.selectedAuction()) {
      return (
        this.selectedAuction().isPreEvent &&
        !this.selectedAuction().eventIsOpen &&
        !this.selectedAuction().isFinished
      );
    } else {
      return false;
    }
  }

  get isPreEventFinished(): boolean {
    return this.inPreEventStatus && !!this.selectedAuction().preEventCloseDate;
    // inPreEventStatus controlla già che l'asta abbia "isFinished" a false e l'evento non sia in corso
  }

  public timeDifferenceFromStart: number;
  private _refreshTimer: any | null = null;
  private readonly _refreshTimerIntervalNotLogged = 20_000;
  private readonly _refreshTimerIntervalLogged = 2000;

  private readonly platformService = inject(PlatformService);
  private readonly imagePreloadService = inject(ImagePreloadService);

  constructor(@Inject(DOCUMENT) private readonly _document: Document) {
    effect((onDestroy) => {
      onDestroy(() => {
        this.metaManagerService.removeNoIndex();
      });
    });
  }

  ngOnInit(): void {
    this.route.params.subscribe((params: Params) => {
      this.id.set(parseInt(params['idAuction'].split('-')[0], 10));
      this.isLoaded.set(false);
      if (isNaN(this.id())) {
        return;
      }

      this.slug.set(params['idAuction'].split('-').slice(1).join('-'));
      this.memorabidAuctionSuggestions = [];
      this.marketplaceAuctionSuggestions = [];

      this.getSuggestedAuctions(this.id());
      this.loadAuctionDetail(this.id());

      this.authService
        .getUserLogged()
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.startTimer();
        });
    });

    this.publicGenericService.getPriceRange().subscribe((priceRangeList) => {
      if (priceRangeList.length > 0) {
        this.updateAuction();
      }
    });
  }

  ngAfterViewInit(): void {}

  onEdit(idAuction: number) {
    if (this.selectedAuction()?.idAuction) {
      if (this.selectedAuction().isCreatedByAdmin) {
        this.redirectService.redirect(`admin/articles/edit/${idAuction}`);
      } else if (this.authService.userLogged.idProfile == 1) {
        this.redirectService.redirect(
          `admin/seller-auctions/edit/${idAuction}`
        );
      } else {
        this.redirectService.redirect(`account/new-auction/${idAuction}`);
      }
    }
  }

  loadAuctionDetail(id: number, withDialog: DialogArticleType = undefined) {
    this.clearRefreshTimer();
    this.isLoaded.set(false);
    const lang = this.languageService.userSelectedLanguage.code;
    const langId = this.languageService.userSelectedLanguage.idLanguage;

    this.auctionViewService
      .getByPk(id)
      .pipe(untilDestroyed(this))
      .subscribe(async (dataR) => {
        const data = { ...dataR, article: { ...dataR.article } };

        if (dataR == null || dataR.idAuction == -1) {
          this.redirectService.redirect('home');
          return;
        }

        // Set page as not crawlable if auction is not visible
        if (!dataR.isVisible) {
          this.metaManagerService.setNoIndex();
        } else {
          this.metaManagerService.removeNoIndex();
        }

        const sl =
          dataR.article.articleInfoList.find(
            (info) => info.languageCode == lang
          )?.slug ??
          slugify(
            dataR.article.articleInfoList.find(
              (info) => info.languageCode == lang
            )?.title ?? '',
            { lower: true, strict: true }
          );

        if (this.slug() !== sl || this.id() !== dataR.idAuction) {
          this.redirectService.redirect(
            `auction/${dataR.idAuction}-${sl}`,
            301
          );
          return;
        }
        try {
          if (dataR.article.articlePhoto360List.length > 0) {
            await this.imagePreloadService.preloadImage(
              dataR.article.articlePhotoList[0].fullPath + '?w=600'
            );
          } else if (dataR.article.articlePhotoList.length > 0) {
            await this.imagePreloadService.preloadImage(
              dataR.article.articlePhotoList[0].fullPath + '?w=600'
            );
          }
        } catch (e) {
          console.error(e);
        }
        this.isLoaded.set(true);

        const infoList = dataR.article.articleInfoList.find(
          (info) => info.languageCode == lang
        );

        data.article.articleTagList = dataR.article.articleTagList.map(
          (tag) => ({
            ...tag,
            tagTranslatedList: tag.tagTranslatedList.filter(
              (t) => t.idLanguage == langId
            ),
          })
        );

        const campaignTitle = dataR.campaignTitleList.find(
          (title) => title.idLanguage == langId
        );
        const promoterTitle = dataR.promoterTitleList.find(
          (title) => title.idLanguage == langId
        );

        if (dataR != null) {
          this.seoService.updateCanonicalUrl(
            `${environment.canonicalUrl}${lang}/auction/${
              dataR.idAuction
            }-${this.slug()}`,
            {
              it: `${environment.canonicalUrl}it/auction/${dataR.idAuction}-${
                dataR.article.articleInfoList.find(
                  (info) => info.languageCode.toUpperCase() == 'IT'
                )?.slug ?? this.slug()
              }`,
              en: `${environment.canonicalUrl}en/auction/${dataR.idAuction}-${
                dataR.article.articleInfoList.find(
                  (info) => info.languageCode.toUpperCase() == 'EN'
                )?.slug ?? this.slug()
              }`,
            }
          );

          data.article.articleInfoList = infoList ? [infoList] : [];
          data.campaignTitleList = campaignTitle ? [campaignTitle] : [];
          data.promoterTitleList = promoterTitle ? [promoterTitle] : [];

          this.sportIds.set(dataR.article.sportTranslationKey ?? '');
          this.competitionIds.set(
            dataR.article.competitionTranslationKey ?? ''
          );
          this.teamIds.set(dataR.article.teamTranslationKey ?? '');
        }

        if (withDialog === 'BID') {
          const ref = this.modalUtils.openModal(BidSuccessDialogComponent, {
            size: 'md',
            keyboard: false,
          });
          ref.componentInstance.selectedAuction = dataR;
        } else if (withDialog === 'BUY_NOW') {
          const ref = this.modalUtils.openModal(BidSuccessDialogComponent, {
            size: 'md',
            keyboard: false,
          });
          ref.componentInstance.isBuyNow = true;
          ref.componentInstance.selectedAuction = dataR;
        }

        this.selectedAuction.set({ ...data });
        this.convertDates();
        this.setMinimumOffer();
        this.setArticleMetaData();
        const startDate = dataR.startDate;
        if (startDate !== '' && startDate !== null) {
          const sd = new Date(startDate + 'Z');
          const timeFromStart =
            new Date().getTime() - (sd || new Date()).getTime();

          if (timeFromStart > 0 && !dataR.isFinished) {
            this.startTimer();
          }
        }
      });
  }

  setMinimumOffer() {
    let found = false;
    for (
      let i = 0;
      !found && i < this.publicGenericService.priceRangeList.length;
      i++
    ) {
      const priceRange = this.publicGenericService.priceRangeList[i];
      const currentPrice =
        this.selectedAuction().currentMaxOfferedPrice != null
          ? this.selectedAuction().currentMaxOfferedPrice
          : this.selectedAuction().basePrice;
      if (
        currentPrice >= priceRange.fromPrice &&
        currentPrice <= priceRange.toPrice
      ) {
        if (this.selectedAuction().currentMaxOfferedPrice == null) {
          this.minimumOffer.set(currentPrice);
        } else {
          this.minimumOffer.set(currentPrice + priceRange.priceValue);
        }
        found = true;
      }
    }
  }

  getSuggestedAuctions(idAuction: number) {
    if (!idAuction) return;

    this.searchService
      .getSuggested(
        this.languageService.userSelectedLanguage.idLanguage,
        idAuction
      )
      .pipe(untilDestroyed(this))
      .subscribe((suggested) => {
        this.memorabidAuctionSuggestions = suggested.memoraBid ?? [];
        this.marketplaceAuctionSuggestions = suggested.marketPlace ?? [];
        this.setArticleMetaData();
      });
  }

  getHistory() {
    this.auctionViewService
      .getHistory(this.selectedAuction().idAuction)
      .pipe(untilDestroyed(this))
      .subscribe((response) => {
        if (response.totalBids > 0) {
          const ref = this.modalUtils.openModal(ArticleOffersDialogComponent, {
            size: 'lg',
            keyboard: false,
          });
          ref.componentInstance.auctionBidInfo.set(response);
          ref.componentInstance.idAuction.set(this.selectedAuction().idAuction);
          ref.result.then(
            () => {
              this.updateAuction();
            },
            () => {
              this.updateAuction();
            }
          );
        }
      });
  }

  reloadAuctionOnBidSuccess(modal: DialogArticleType) {
    this.loadAuctionDetail(this.id(), modal);
  }

  deleteAutoRebid(data: { idAutoRebid: number }) {
    this._api
      .request(ApiServiceHolder.auction.deleteAutoRebid(data.idAutoRebid))
      .subscribe(() => {
        this.loadAuctionDetail(this.id());
      });
  }

  contact() {
    if (this.authService.isAuthenticated() == true) {
      const ref = this.modalUtils.openModal(ContactsDialogComponent, {
        size: 'lg',
        keyboard: false,
      });
      ref.componentInstance.isMemorabid = this.isMemorabid();
      ref.componentInstance.inPreEventStatus = this.inPreEventStatus;
      ref.componentInstance.idAuction = this.id();
    } else {
    }
  }

  ngOnDestroy() {
    this.metaManagerService.updateTitleAndMeta('', '', '', '');
    this.clearRefreshTimer();
  }

  stopRefresh() {
    this.clearRefreshTimer();
    this.updateAuction();
  }

  startTimer() {
    if (this.selectedAuction().isFinished || this.selectedAuction().buyItNow) {
      return;
    }

    this.platformService.run({
      browser: () => {
        if (this._refreshTimer) {
          this._refreshTimer.unsubscribe();
        }

        this._refreshTimer = timer(
          0,
          this.authService.isAuthenticated()
            ? this._refreshTimerIntervalLogged
            : this._refreshTimerIntervalNotLogged
        )
          .pipe(untilDestroyed(this))
          .subscribe(() => {
            this.updateAuction();
          });
      },
    });
  }

  updateAuction() {
    if (!this.selectedAuction() || this.selectedAuction().idAuction == -1) {
      return null;
    }

    this.eventService
      .getAuctionPriceInfo(this.selectedAuction().idAuction)
      .pipe(untilDestroyed(this))
      .subscribe((dataR) => {
        this.selectedAuction.update((auction) => ({
          ...auction,
          ...dataR,
        }));

        // Set page as not crawlable if auction is not visible (on update)
        if (!this.selectedAuction().isVisible) {
          this.metaManagerService.setNoIndex();
        } else {
          this.metaManagerService.removeNoIndex();
        }

        this.convertDates();
        this.setMinimumOffer();

        if (this.selectedAuction().isFinished) {
          this.clearRefreshTimer();
        }
      });
  }

  convertDates() {
    const startDateUTC = new Date(this.selectedAuction().startDate + 'Z');
    let newEndDate = new Date();

    if (this.selectedAuction().endDateView == null) {
      // add duration to start date
      newEndDate = new Date(
        startDateUTC.getTime() + this.selectedAuction().durationMinutes * 60000
      );
    } else {
      newEndDate = new Date(this.selectedAuction().endDateView + ':00Z');
    }

    this.selectedAuction.update((auction) => ({
      ...auction,
      startDateView: this.datePipe.transform(startDateUTC, 'yyyy-MM-ddTHH:mm'),
      endDateView: this.datePipe.transform(newEndDate, 'yyyy-MM-ddTHH:mm'),
    }));
  }

  private clearRefreshTimer(): void {
    if (this._refreshTimer) {
      this._refreshTimer.unsubscribe();
      this._refreshTimer = null;
    }
  }

  private setArticleMetaData(): void {
    let articleTitle = '';
    let articlePhoto = '';
    let articleDescription = '';

    if (
      this.selectedAuction().article.articleInfoList.length > 0 &&
      this.selectedAuction().article.articleInfoList[0]
    ) {
      articleTitle = this.selectedAuction().article.articleInfoList[0].title;
      articleDescription =
        this.selectedAuction().article.articleInfoList[0].description.replace(
          /<[^>]+>/g,
          ''
        );
    }

    if (this.selectedAuction().article.articlePhotoList.length > 0) {
      articlePhoto =
        this.selectedAuction().article.articlePhotoList[0].fullPath;
    }

    this.metaManagerService.updateTitleAndMeta(
      articleTitle,
      articleDescription,
      environment.APPOrigin +
        '/' +
        this.languageService.userSelectedLanguage.code +
        '/auction/' +
        this.selectedAuction().idAuction +
        '-' +
        this.slug(),
      articlePhoto
    );

    const buildJsonLd = (article: AuctionView) => {
      return `
        {
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "${article.title.replace(/"/g, '\\"')}",
            "image": ["${article.articlePhoto.fullPath}"],
            "offers": {
              "@type": "Offer",
              "url": "${environment.canonicalUrl}${
        this.languageService.userSelectedLanguage.code
      }/auction/${article.slug}",
              "priceCurrency": "EUR",
              "price": "${(
                article.currentMaxOfferedPrice ?? article.basePrice
              ).toFixed(2)}",
              "image": "${article.articlePhoto.fullPath}",
              "seller": {
                  "@type": "Organization",
                  "name": "Memorabid",
                  "url": "${environment.canonicalUrl}${
        this.languageService.userSelectedLanguage.code
      }"
              }
            }
        }
      `;
    };

    const related = this.memorabidAuctionSuggestions
      .map(buildJsonLd)
      .concat(this.marketplaceAuctionSuggestions.map(buildJsonLd));

    let script =
      (this._document.getElementById('json-ld-auction') as HTMLScriptElement) ??
      this._document.createElement('script');
    script.type = `application/ld+json`;
    script.id = `json-ld-auction`;
    script.text = `
        {
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "${articleTitle.replace(/"/g, '\\"')}",
            "image": [${this.selectedAuction()
              .article.articlePhotoList.map((photo) => `"${photo.fullPath}"`)
              .join(', ')}],
            "description": "${articleDescription.replace(/"/g, '\\"')}",
            "keywords": [${this.selectedAuction()
              .article.articleTagList.map(
                (tag) =>
                  `"${environment.canonicalUrl}${this.languageService.userSelectedLanguage.code}/tags/${tag.description}"`
              )
              .join(', ')}],
            "isRelatedTo": [${related.join(', ')}],
            "offers": {
              "@type": "Offer",
              "url": "${environment.canonicalUrl}${
      this.languageService.userSelectedLanguage.code
    }/auction/${this.selectedAuction().idAuction}-${this.slug()}",
              "priceCurrency": "EUR",
              "price": "${(
                this.selectedAuction().currentMaxOfferedPrice ??
                this.selectedAuction().basePrice
              ).toFixed(2)}",
              "availability": "https://schema.org/InStock",
              "image": "${articlePhoto}",
              "seller": {
                  "@type": "Organization",
                  "name": "Memorabid",
                  "url": "${environment.canonicalUrl}${
      this.languageService.userSelectedLanguage.code
    }"
              }
            }
        }
    `;

    this._document.head.appendChild(script);
  }
}
